import { Texto, Vertical } from '@gravitate-js/excalibrr'
import { theme } from 'antd'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { AttachmentChip } from './AttachmentChip'
import { InlineChart } from './InlineChart'
import { InlineImage } from './InlineImage'

export type MessageBubbleOutputFile = {
  filename: string
  mime_type: string
  size_bytes: number
}

export type MessageBubbleProps = {
  messageRole: 'user' | 'assistant'
  text: string
  pending?: boolean
  taskId?: string
  outputFiles?: MessageBubbleOutputFile[]
  attachments?: Array<{
    filename: string
    mime_type: string
    size_bytes: number
    content_b64?: string | null
    url?: string | null
  }>
  // Wall-clock time the turn took (user submit → assistant reply), in ms.
  durationMs?: number
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(1, Math.round(ms / 1000))
  if (totalSeconds < 60) return `${totalSeconds}s`
  return `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`
}

export function MessageBubble({
  messageRole,
  text,
  pending,
  taskId,
  outputFiles,
  attachments,
  durationMs,
}: MessageBubbleProps) {
  const { token } = theme.useToken()
  const isUser = messageRole === 'user'
  const files = outputFiles ?? []
  const atts = attachments ?? []
  // Only inline-renderable images are shown in the bubble today; filtering here
  // avoids emitting an empty wrapper for url-only/non-image attachments.
  const visibleAtts = atts.filter(
    (a) => a.content_b64 && a.mime_type.startsWith('image/'),
  )
  const hasMedia = files.length > 0 || visibleAtts.length > 0
  return (
    <div
      style={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        background: isUser ? token.colorPrimaryBg : token.colorFillTertiary,
        padding: '8px 12px',
        borderRadius: 8,
        // Charts/images fill the full message column; text bubbles stay at a
        // readable 85% so short replies don't stretch edge-to-edge.
        maxWidth: hasMedia ? '100%' : '85%',
        width: hasMedia ? '100%' : undefined,
        boxSizing: 'border-box',
      }}
    >
      <Vertical gap={4}>
        {isUser ? (
          <Texto>{text}</Texto>
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
        )}
        {visibleAtts.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              marginTop: 4,
            }}
          >
            {visibleAtts.map((a, i) => (
              <InlineImage
                key={`${a.filename}-${i}`}
                filename={a.filename}
                src={`data:${a.mime_type};base64,${a.content_b64}`}
              />
            ))}
          </div>
        )}
        {files.length > 0 && taskId && (
          // Plain div, not excalibrr Vertical — its forced overflow:hidden clips
          // the attachment card's download button at the bubble edge.
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              marginTop: 8,
            }}
          >
            {files.map((f) => {
              if (f.filename.endsWith('.plotly.json')) {
                return (
                  <InlineChart
                    key={f.filename}
                    taskId={taskId}
                    filename={f.filename}
                    mimeType={f.mime_type}
                    sizeBytes={f.size_bytes}
                  />
                )
              }
              if (f.mime_type.startsWith('image/')) {
                return (
                  <InlineImage
                    key={f.filename}
                    taskId={taskId}
                    filename={f.filename}
                  />
                )
              }
              return (
                <AttachmentChip
                  key={f.filename}
                  taskId={taskId}
                  filename={f.filename}
                  mimeType={f.mime_type}
                  sizeBytes={f.size_bytes}
                />
              )
            })}
          </div>
        )}
        {pending && (
          <Texto appearance='hint' style={{ fontSize: '11px' }}>
            …
          </Texto>
        )}
        {durationMs !== undefined && (
          <Texto
            appearance='hint'
            style={{ fontSize: '10px', alignSelf: 'flex-end' }}
          >
            {formatDuration(durationMs)}
          </Texto>
        )}
      </Vertical>
    </div>
  )
}
